import React, { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  TextField, Button, IconButton, Chip, Typography, Grid,
  CircularProgress, Collapse, Box, Select, MenuItem,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Paper, ClickAwayListener, Tooltip, Checkbox,
  FormControlLabel, InputAdornment, Slider
} from '@material-ui/core'
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Add as AddIcon, Search as SearchIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Warning as WarningIcon, CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@material-ui/icons'

/* ─── Constants ────────────────────────────────────────── */

const CONTAINER_SIZES = [10, 20, 30, 40]
const CONTAINER_LABELS = { 10: '10 CY', 20: '20 CY', 30: '30 CY', 40: '40 CY' }
const CONTAINER_COLORS = {
  10: { bg: 'var(--primary-soft, #e3eee0)', color: 'var(--primary, #1f5e3a)', border: 'rgba(31,94,58,.25)' },
  20: { bg: 'var(--labor-soft, #e6ebfa)', color: 'var(--labor, #3454c1)', border: 'rgba(52,84,193,.25)' },
  30: { bg: 'var(--equipment-soft, #fbe8d6)', color: 'var(--equipment, #b35d1a)', border: 'rgba(179,93,26,.25)' },
  40: { bg: 'var(--disposal-soft, #f7dccf)', color: 'var(--disposal, #a9341d)', border: 'rgba(169,52,29,.25)' }
}

const WASTE_TYPES = [
  'concrete', 'dirt', 'rock', 'asphalt', 'tile',
  'green_waste', 'c_and_d', 'msw',
  'appliances', 'electronics', 'tires', 'scrap_metal',
  'special_waste', 'sludge', 'contaminated_soil'
]

const WASTE_COLORS = {
  concrete: { bg: '#eef0f1', color: '#546e7a' },
  dirt: { bg: '#f0ebe6', color: '#6d4c41' },
  rock: { bg: '#eceff1', color: '#607d8b' },
  asphalt: { bg: '#e8ecef', color: '#455a64' },
  tile: { bg: '#f0f2f3', color: '#78909c' },
  green_waste: { bg: 'var(--material-soft, #dceedf)', color: 'var(--material, #2f7a3e)' },
  c_and_d: { bg: 'var(--equipment-soft, #fbe8d6)', color: 'var(--equipment, #b35d1a)' },
  msw: { bg: 'var(--disposal-soft, #f7dccf)', color: 'var(--disposal, #a9341d)' },
  appliances: { bg: 'var(--subcontract-soft, #ece2fa)', color: 'var(--subcontract, #6e3fb7)' },
  electronics: { bg: 'var(--labor-soft, #e6ebfa)', color: 'var(--labor, #3454c1)' },
  tires: { bg: '#e8e8e8', color: '#424242' },
  scrap_metal: { bg: '#ececec', color: '#757575' },
  special_waste: { bg: '#fce4ec', color: '#c62828' },
  sludge: { bg: '#efebe9', color: '#5d4037' },
  contaminated_soil: { bg: '#fbe9e7', color: '#bf360c' }
}

const WASTE_LABELS = {
  concrete: 'Concrete', dirt: 'Dirt', rock: 'Rock', asphalt: 'Asphalt',
  tile: 'Tile', green_waste: 'Green Waste', c_and_d: 'C&D', msw: 'MSW',
  appliances: 'Appliances', electronics: 'Electronics', tires: 'Tires',
  scrap_metal: 'Scrap Metal', special_waste: 'Special Waste',
  sludge: 'Sludge', contaminated_soil: 'Contaminated Soil'
}

const SIX_MONTHS_MS = 1000 * 60 * 60 * 24 * 180

/* ─── Effective Cost Calculator ───────────────────────── */

const calcEffectiveCost = (item, hauls = 1, tonsPerHaul = null) => {
  const rental = Number(item.rentalRate) || 0
  const haul = Number(item.haulFee) || 0
  const dumpFlat = Number(item.dumpFee) || 0
  const dumpPerTon = Number(item.dumpFeePerTon) || 0
  const included = Number(item.includedTons) || 0
  const overage = Number(item.overagePerTon) || 0

  const tons = tonsPerHaul != null ? tonsPerHaul : included
  const overageCost = included > 0 && tons > included ? (tons - included) * overage : 0
  const dumpCost = dumpPerTon > 0 ? dumpPerTon * tons : dumpFlat

  return rental + (haul + dumpCost + overageCost) * hauls
}

const getPricingType = (item) => {
  const hasDump = (Number(item.dumpFee) || 0) > 0 || (Number(item.dumpFeePerTon) || 0) > 0
  const hasRental = (Number(item.rentalRate) || 0) > 0
  if (!hasDump && !hasRental) return 'all-in'
  if (hasDump && hasRental) return 'split'
  if (hasDump) return 'haul+dump'
  return 'rental+haul'
}

const PRICING_TYPE_LABELS = {
  'all-in': 'All-in',
  'split': 'Split',
  'haul+dump': 'Haul + Dump',
  'rental+haul': 'Rental + Haul'
}

const PRICING_TYPE_COLORS = {
  'all-in': { bg: 'var(--primary-soft, #e3eee0)', color: 'var(--primary, #1f5e3a)' },
  'split': { bg: 'var(--equipment-soft, #fbe8d6)', color: 'var(--equipment, #b35d1a)' },
  'haul+dump': { bg: 'var(--accent-soft, #f7e4d6)', color: 'var(--accent, #c66a45)' },
  'rental+haul': { bg: 'var(--labor-soft, #e6ebfa)', color: 'var(--labor, #3454c1)' }
}

const mono = { fontFamily: 'var(--font-mono)' }
const $ = (n) => `$${Number(n || 0).toFixed(0)}`
const $2 = (n) => `$${Number(n || 0).toFixed(2)}`

/* ─── RateFormModal ────────────────────────────────────── */

const RateFormModal = ({ open, onClose, onSave, editItem, url }) => {
  const isEdit = !!editItem
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [vendorResults, setVendorResults] = useState([])
  const [showVendorResults, setShowVendorResults] = useState(false)
  const vendorTimer = useRef(null)

  useEffect(() => {
    if (!open) return
    if (editItem) {
      setForm({
        vendor: editItem.vendor || '',
        vendorName: editItem.vendorName || '',
        containerSize: editItem.containerSize || 20,
        rentalRate: editItem.rentalRate || 0,
        rentalPeriodDays: editItem.rentalPeriodDays || 7,
        extraDayRate: editItem.extraDayRate || 0,
        haulFee: editItem.haulFee || editItem.flatRatePerHaul || '',
        dumpFee: editItem.dumpFee || 0,
        dumpFeePerTon: editItem.dumpFeePerTon || 0,
        includedTons: editItem.includedTons || 0,
        overagePerTon: editItem.overagePerTon || 0,
        acceptedWaste: editItem.acceptedWaste || [],
        notes: editItem.notes || '',
        quoteDate: editItem.quoteDate ? editItem.quoteDate.substring(0, 10) : '',
        quotedBy: editItem.quotedBy || ''
      })
    } else {
      setForm({
        vendor: '', vendorName: '', containerSize: 20,
        rentalRate: 0, rentalPeriodDays: 7, extraDayRate: 0,
        haulFee: '', dumpFee: 0, dumpFeePerTon: 0,
        includedTons: 0, overagePerTon: 0,
        acceptedWaste: [], notes: '', quoteDate: '', quotedBy: ''
      })
    }
    setVendorResults([])
    setShowVendorResults(false)
  }, [open, editItem])

  const handleVendorSearch = (val) => {
    setForm(f => ({ ...f, vendorName: val, vendor: '' }))
    clearTimeout(vendorTimer.current)
    if (val.length < 2) { setVendorResults([]); setShowVendorResults(false); return }
    vendorTimer.current = setTimeout(() => {
      axios.get(`${url}/api/bid-estimation/rolloff-rates/vendor-lookup`, { params: { q: val } })
        .then(res => { setVendorResults(res.data.results || []); setShowVendorResults(true) })
        .catch(() => {})
    }, 300)
  }

  const selectVendor = (v) => {
    setForm(f => ({ ...f, vendor: v._id, vendorName: v.name }))
    setShowVendorResults(false)
  }

  const toggleWaste = (w) => {
    setForm(f => {
      const arr = f.acceptedWaste.includes(w)
        ? f.acceptedWaste.filter(x => x !== w)
        : [...f.acceptedWaste, w]
      return { ...f, acceptedWaste: arr }
    })
  }

  const handleSubmit = async () => {
    if (!form.vendor || !form.haulFee) return
    setSaving(true)
    try {
      const payload = {
        vendor: form.vendor,
        containerSize: form.containerSize,
        rentalRate: Number(form.rentalRate) || 0,
        rentalPeriodDays: Number(form.rentalPeriodDays) || 7,
        extraDayRate: Number(form.extraDayRate) || 0,
        haulFee: Number(form.haulFee),
        dumpFee: Number(form.dumpFee) || 0,
        dumpFeePerTon: Number(form.dumpFeePerTon) || 0,
        includedTons: Number(form.includedTons) || 0,
        overagePerTon: Number(form.overagePerTon) || 0,
        acceptedWaste: form.acceptedWaste,
        notes: form.notes || undefined,
        quoteDate: form.quoteDate || undefined,
        quotedBy: form.quotedBy || undefined
      }
      if (isEdit) {
        await axios.put(`${url}/api/bid-estimation/rolloff-rates/${editItem._id}`, payload)
      } else {
        await axios.post(`${url}/api/bid-estimation/rolloff-rates`, payload)
      }
      onSave()
      onClose()
    } catch (err) {
      const msg = err.response?.data?.error || err.message
      alert(msg)
    } finally {
      setSaving(false)
    }
  }

  const effectivePreview = calcEffectiveCost(form, 1)

  const SectionLabel = ({ children }) => (
    <div style={{
      fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em',
      color: 'var(--ink-3, #6f7a6b)', marginTop: 18, marginBottom: 8,
      fontFamily: 'var(--font-mono)', borderBottom: '1px solid var(--line, #e0d8c2)', paddingBottom: 4
    }}>{children}</div>
  )

  const NumField = ({ label, field, prefix, placeholder, xs = 4 }) => (
    <Grid item xs={xs}>
      <TextField fullWidth variant="outlined" size="small" label={label}
        type="number" value={form[field] || ''}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        placeholder={placeholder || '0'}
        inputProps={{ min: 0, step: 0.01 }}
        InputProps={prefix ? { startAdornment: <InputAdornment position="start">{prefix}</InputAdornment> } : undefined}
      />
    </Grid>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--primary, #1f5e3a), var(--primary-2, #164a2c))',
        padding: '18px 24px', color: '#fbf8f1'
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.3 }}>
          {isEdit ? 'Edit Roll-off Rate' : 'Add Roll-off Rate'}
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
          Enter the vendor's quoted pricing structure
        </div>
      </div>
      <DialogContent style={{ paddingTop: 16, background: 'var(--surface, #fbf8f1)' }}>
        {/* Vendor Lookup */}
        <Typography variant="caption" style={{ color: 'var(--ink-3, #6f7a6b)' }}>Vendor</Typography>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <TextField
            fullWidth variant="outlined" size="small" placeholder="Search vendors..."
            value={form.vendorName || ''}
            onChange={e => handleVendorSearch(e.target.value)}
            InputProps={{
              endAdornment: form.vendor
                ? <Chip size="small" label="Selected" style={{ background: 'var(--primary-soft, #e3eee0)', color: 'var(--primary, #1f5e3a)' }} />
                : null
            }}
          />
          {showVendorResults && vendorResults.length > 0 && (
            <ClickAwayListener onClickAway={() => setShowVendorResults(false)}>
              <Paper elevation={4} style={{
                position: 'absolute', zIndex: 10, width: '100%', maxHeight: 200, overflow: 'auto',
                border: '1px solid var(--line, #e0d8c2)', borderRadius: 10
              }}>
                {vendorResults.map(v => (
                  <Box key={v._id} px={2} py={1} style={{
                    cursor: 'pointer', borderBottom: '1px solid var(--line, #e0d8c2)',
                    transition: 'background .1s ease'
                  }}
                    onClick={() => selectVendor(v)}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-2, #f6f1e3)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = '' }}
                  >
                    <Typography variant="body2" style={{ fontWeight: 600 }}>{v.name}</Typography>
                    {v.phoneNumber && <Typography variant="caption" style={{ color: 'var(--ink-3, #6f7a6b)' }}>{v.phoneNumber}</Typography>}
                  </Box>
                ))}
              </Paper>
            </ClickAwayListener>
          )}
        </div>

        {/* Container Size */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Container Size</InputLabel>
              <Select label="Container Size" value={form.containerSize || 20}
                onChange={e => setForm(f => ({ ...f, containerSize: e.target.value }))}>
                {CONTAINER_SIZES.map(s => (
                  <MenuItem key={s} value={s}>{s} CY</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Rental */}
        <SectionLabel>Rental</SectionLabel>
        <Grid container spacing={2}>
          <NumField label="Rental Rate" field="rentalRate" prefix="$" xs={4} />
          <NumField label="Days Included" field="rentalPeriodDays" xs={4} />
          <NumField label="Extra $/Day" field="extraDayRate" prefix="$" xs={4} />
        </Grid>

        {/* Haul & Dump */}
        <SectionLabel>Haul & Dump Fees</SectionLabel>
        <Grid container spacing={2}>
          <NumField label="Haul Fee (per pickup)" field="haulFee" prefix="$" xs={6} />
          <NumField label="Dump Fee (per load)" field="dumpFee" prefix="$" xs={6} />
          <NumField label="Dump Fee (per ton)" field="dumpFeePerTon" prefix="$" xs={4} />
          <NumField label="Included Tons" field="includedTons" xs={4} />
          <NumField label="Overage $/Ton" field="overagePerTon" prefix="$" xs={4} />
        </Grid>

        {/* Effective Preview */}
        {Number(form.haulFee) > 0 && (
          <div style={{
            marginTop: 16, padding: '12px 14px', borderRadius: 10,
            background: 'var(--primary-soft, #e3eee0)', border: '1px solid rgba(31,94,58,.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary, #1f5e3a)' }}>
                Effective cost (1 haul, at included weight)
              </span>
              <span style={{ ...mono, fontSize: 18, fontWeight: 700, color: 'var(--primary, #1f5e3a)' }}>
                {$2(effectivePreview)}
              </span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3, #6f7a6b)', marginTop: 4 }}>
              {Number(form.rentalRate) > 0 ? `${$(form.rentalRate)} rental + ` : ''}
              {$(form.haulFee)}/haul
              {Number(form.dumpFee) > 0 ? ` + ${$(form.dumpFee)} dump` : ''}
              {Number(form.dumpFeePerTon) > 0 ? ` + ${$(form.dumpFeePerTon)}/ton dump` : ''}
              {Number(form.includedTons) > 0 ? ` \u00b7 ${form.includedTons}T incl` : ''}
              {Number(form.overagePerTon) > 0 ? `, then ${$(form.overagePerTon)}/ton` : ''}
            </div>
          </div>
        )}

        {/* Accepted Waste Types */}
        <SectionLabel>Accepted Waste</SectionLabel>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {WASTE_TYPES.map(w => {
            const selected = (form.acceptedWaste || []).includes(w)
            const wc = WASTE_COLORS[w] || { bg: '#f0f0f0', color: '#666' }
            return (
              <Chip key={w} size="small" label={WASTE_LABELS[w]}
                onClick={() => toggleWaste(w)}
                style={{
                  background: selected ? wc.color : 'var(--surface-2, #f6f1e3)',
                  color: selected ? '#fff' : 'var(--ink-3, #6f7a6b)',
                  border: selected ? 'none' : '1px solid var(--line, #e0d8c2)',
                  fontWeight: selected ? 600 : 400,
                  cursor: 'pointer'
                }}
              />
            )
          })}
        </div>

        {/* Quote Info */}
        <SectionLabel>Quote Info</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField fullWidth variant="outlined" size="small" label="Quote Date"
              type="date" value={form.quoteDate || ''}
              onChange={e => setForm(f => ({ ...f, quoteDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth variant="outlined" size="small" label="Quoted By"
              value={form.quotedBy || ''}
              onChange={e => setForm(f => ({ ...f, quotedBy: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth variant="outlined" size="small" label="Notes"
              multiline rows={2} value={form.notes || ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 24px',
        borderTop: '1px solid var(--line, #e0d8c2)', background: 'var(--surface-2, #f6f1e3)'
      }}>
        <Button onClick={onClose} style={{ color: 'var(--ink-3, #6f7a6b)' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !form.vendor || !form.haulFee}
          style={{ background: 'var(--primary, #1f5e3a)', color: '#fbf8f1', borderRadius: 10 }}>
          {saving ? <CircularProgress size={20} style={{ color: '#fbf8f1' }} /> : (isEdit ? 'Update' : 'Add Rate')}
        </Button>
      </div>
    </Dialog>
  )
}

/* ─── Rate Details Panel (expandable row) ──────────────── */

const RateDetailsPanel = ({ item }) => {
  const effective1 = calcEffectiveCost(item, 1)
  const effective3 = calcEffectiveCost(item, 3)
  const pricingType = getPricingType(item)
  const ptc = PRICING_TYPE_COLORS[pricingType]
  const labelStyle = { fontWeight: 600, width: 150, color: 'var(--ink-2, #3d463a)', fontSize: 12 }
  const valStyle = { ...mono, fontSize: 13 }

  return (
    <Box p={2} style={{ background: 'var(--surface-2, #f6f1e3)' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <div style={{ ...mono, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-3, #6f7a6b)', marginBottom: 8 }}>Pricing Breakdown</div>
          <Table size="small">
            <TableBody>
              {Number(item.rentalRate) > 0 && (
                <TableRow><TableCell style={labelStyle}>Rental</TableCell><TableCell style={valStyle}>{$(item.rentalRate)} / {item.rentalPeriodDays}d</TableCell></TableRow>
              )}
              <TableRow><TableCell style={labelStyle}>Haul Fee</TableCell><TableCell style={{ ...valStyle, fontWeight: 700, color: 'var(--accent, #c66a45)' }}>{$2(item.haulFee)}</TableCell></TableRow>
              {Number(item.dumpFee) > 0 && (
                <TableRow><TableCell style={labelStyle}>Dump Fee</TableCell><TableCell style={valStyle}>{$2(item.dumpFee)} / load</TableCell></TableRow>
              )}
              {Number(item.dumpFeePerTon) > 0 && (
                <TableRow><TableCell style={labelStyle}>Dump Fee</TableCell><TableCell style={valStyle}>{$2(item.dumpFeePerTon)} / ton</TableCell></TableRow>
              )}
              {Number(item.includedTons) > 0 && (
                <TableRow><TableCell style={labelStyle}>Included Weight</TableCell><TableCell style={valStyle}>{item.includedTons} tons</TableCell></TableRow>
              )}
              {Number(item.overagePerTon) > 0 && (
                <TableRow><TableCell style={labelStyle}>Overage</TableCell><TableCell style={valStyle}>{$(item.overagePerTon)} / ton over</TableCell></TableRow>
              )}
              {Number(item.extraDayRate) > 0 && (
                <TableRow><TableCell style={labelStyle}>Extra Day</TableCell><TableCell style={valStyle}>{$(item.extraDayRate)} / day</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Grid>
        <Grid item xs={12} md={4}>
          <div style={{ ...mono, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-3, #6f7a6b)', marginBottom: 8 }}>Effective Cost</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--surface, #fbf8f1)', border: '1px solid var(--line, #e0d8c2)' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3, #6f7a6b)' }}>1 haul</div>
              <div style={{ ...mono, fontSize: 20, fontWeight: 700, color: 'var(--accent, #c66a45)' }}>{$2(effective1)}</div>
            </div>
            <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--surface, #fbf8f1)', border: '1px solid var(--line, #e0d8c2)' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3, #6f7a6b)' }}>3 hauls</div>
              <div style={{ ...mono, fontSize: 20, fontWeight: 700, color: 'var(--accent, #c66a45)' }}>{$2(effective3)}</div>
              <div style={{ fontSize: 10, color: 'var(--ink-4, #97a094)' }}>{$2(effective3 / 3)} per haul</div>
            </div>
          </div>
        </Grid>
        <Grid item xs={12} md={4}>
          <div style={{ ...mono, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-3, #6f7a6b)', marginBottom: 8 }}>Details</div>
          <Table size="small">
            <TableBody>
              <TableRow><TableCell style={labelStyle}>Type</TableCell><TableCell><Chip size="small" label={PRICING_TYPE_LABELS[pricingType]} style={{ background: ptc.bg, color: ptc.color, fontWeight: 600, fontSize: 10 }} /></TableCell></TableRow>
              {item.quotedBy && <TableRow><TableCell style={labelStyle}>Quoted By</TableCell><TableCell style={{ fontSize: 12 }}>{item.quotedBy}</TableCell></TableRow>}
              {item.quoteDate && <TableRow><TableCell style={labelStyle}>Quote Date</TableCell><TableCell style={{ fontSize: 12 }}>{new Date(item.quoteDate).toLocaleDateString()}</TableCell></TableRow>}
              {item.notes && <TableRow><TableCell style={labelStyle}>Notes</TableCell><TableCell style={{ fontSize: 12, color: 'var(--ink-3, #6f7a6b)' }}>{item.notes}</TableCell></TableRow>}
            </TableBody>
          </Table>
          <div style={{ marginTop: 12 }}>
            <div style={{ ...mono, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-3, #6f7a6b)', marginBottom: 6 }}>Accepted Waste</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(item.acceptedWaste || []).length === 0
                ? <span style={{ fontSize: 12, color: 'var(--ink-4, #97a094)' }}>None specified</span>
                : (item.acceptedWaste || []).map(w => {
                  const wc = WASTE_COLORS[w] || { bg: '#f0f0f0', color: '#666' }
                  return (
                    <Chip key={w} size="small" label={WASTE_LABELS[w] || w}
                      style={{ background: wc.bg, color: wc.color, fontWeight: 600, fontSize: 10, border: `1px solid ${wc.color}22` }}
                    />
                  )
                })}
            </div>
          </div>
        </Grid>
      </Grid>
    </Box>
  )
}

/* ─── Main Component ───────────────────────────────────── */

const RolloffRatesTable = ({ url }) => {
  const [rates, setRates] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [sizeFilter, setSizeFilter] = useState('All')
  const [vendorFilter, setVendorFilter] = useState('All')
  const [wasteFilter, setWasteFilter] = useState('All')
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalItem, setModalItem] = useState(null)
  const [editingPriceId, setEditingPriceId] = useState(null)
  const [editingPriceVal, setEditingPriceVal] = useState('')
  const [seeding, setSeeding] = useState(false)
  const [seedResult, setSeedResult] = useState(null)
  const [compareHauls, setCompareHauls] = useState(1)

  useEffect(() => { fetchRates(); fetchSummary() }, []) // eslint-disable-line

  const fetchRates = () => {
    setLoading(true)
    axios.get(`${url}/api/bid-estimation/rolloff-rates`).then(res => {
      setRates(res.data || []); setLoading(false)
    }).catch(err => { console.error(err); setLoading(false) })
  }

  const fetchSummary = () => {
    axios.get(`${url}/api/bid-estimation/rolloff-rates/summary`).then(res => {
      setSummary(res.data)
    }).catch(console.error)
  }

  const filteredRates = React.useMemo(() => {
    let result = rates
    if (sizeFilter !== 'All') result = result.filter(r => r.containerSize === Number(sizeFilter))
    if (vendorFilter !== 'All') result = result.filter(r => r.vendor === vendorFilter)
    if (wasteFilter !== 'All') result = result.filter(r => (r.acceptedWaste || []).includes(wasteFilter))
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(r =>
        (r.vendorName || '').toLowerCase().includes(term) ||
        (r.notes || '').toLowerCase().includes(term)
      )
    }
    return result
  }, [rates, sizeFilter, vendorFilter, wasteFilter, searchTerm])

  const distinctVendors = React.useMemo(() => {
    const map = new Map()
    rates.forEach(r => { if (r.vendor && r.vendorName) map.set(r.vendor, r.vendorName) })
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))
  }, [rates])

  const handleDelete = () => {
    if (!deleteTarget) return
    axios.delete(`${url}/api/bid-estimation/rolloff-rates/${deleteTarget._id}`)
      .then(() => { setDeleteTarget(null); fetchRates(); fetchSummary() }).catch(console.error)
  }

  const handleInlinePriceSave = useCallback((id) => {
    const val = Number(editingPriceVal)
    if (!val || val <= 0) { setEditingPriceId(null); return }
    axios.patch(`${url}/api/bid-estimation/rolloff-rates/${id}/quote`, { haulFee: val })
      .then(() => { setEditingPriceId(null); fetchRates(); fetchSummary() })
      .catch(err => { console.error(err); setEditingPriceId(null) })
  }, [editingPriceVal, url]) // eslint-disable-line

  const handleSeed = () => {
    setSeeding(true); setSeedResult(null)
    axios.post(`${url}/api/bid-estimation/rolloff-rates/seed`)
      .then(res => { setSeedResult(res.data); setSeeding(false); fetchRates(); fetchSummary() })
      .catch(err => { setSeedResult({ error: err.response?.data?.error || err.message }); setSeeding(false) })
  }

  const isStaleQuote = (quoteDate) => {
    if (!quoteDate) return true
    return Date.now() - new Date(quoteDate).getTime() > SIX_MONTHS_MS
  }

  const relativeDate = (dateStr) => {
    if (!dateStr) return '\u2014'
    const d = new Date(dateStr)
    const diff = Date.now() - d.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    if (days < 1) return 'Today'
    if (days < 30) return `${days}d ago`
    if (days < 365) return `${Math.floor(days / 30)}mo ago`
    return `${Math.floor(days / 365)}y ago`
  }

  return (
    <div style={{ padding: 16 }}>
      {/* Summary Stats */}
      {summary && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Vendors', value: summary.totalVendors || 0 },
            { label: 'Rates', value: summary.totalRates || 0 },
            { label: 'Avg 20 CY', value: summary.avgBySize?.[20] ? `$${summary.avgBySize[20].toFixed(0)}` : '\u2014' },
            { label: 'Avg 40 CY', value: summary.avgBySize?.[40] ? `$${summary.avgBySize[40].toFixed(0)}` : '\u2014' }
          ].map(s => (
            <div key={s.label} style={{
              padding: '14px 22px', textAlign: 'center',
              background: 'var(--surface, #fbf8f1)', border: '1px solid var(--line, #e0d8c2)',
              borderRadius: 'var(--r-lg, 14px)', boxShadow: 'var(--shadow-sm)',
              minWidth: 110
            }}>
              <div style={{
                fontSize: 22, fontWeight: 700, color: 'var(--accent, #c66a45)',
                fontFamily: 'var(--font-mono)'
              }}>{s.value}</div>
              <div style={{
                fontSize: 10, color: 'var(--ink-4, #97a094)', textTransform: 'uppercase',
                letterSpacing: '0.08em', fontWeight: 600, marginTop: 2,
                fontFamily: 'var(--font-mono)'
              }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filter Row */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
          <InputLabel>Size</InputLabel>
          <Select label="Size" value={sizeFilter} onChange={e => setSizeFilter(e.target.value)}>
            <MenuItem value="All">All Sizes</MenuItem>
            {CONTAINER_SIZES.map(s => <MenuItem key={s} value={s}>{s} CY</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Vendor</InputLabel>
          <Select label="Vendor" value={vendorFilter} onChange={e => setVendorFilter(e.target.value)}>
            <MenuItem value="All">All Vendors</MenuItem>
            {distinctVendors.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Waste Type</InputLabel>
          <Select label="Waste Type" value={wasteFilter} onChange={e => setWasteFilter(e.target.value)}>
            <MenuItem value="All">All Types</MenuItem>
            {WASTE_TYPES.map(w => <MenuItem key={w} value={w}>{WASTE_LABELS[w]}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField variant="outlined" size="small" placeholder="Search..." value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)} style={{ minWidth: 160 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" style={{ color: 'var(--ink-4, #97a094)' }} /></InputAdornment> }}
        />

        <div style={{ flex: 1 }} />

        <Button variant="outlined" size="small"
          startIcon={seeding ? <CircularProgress size={14} /> : null}
          onClick={handleSeed} disabled={seeding}
          style={{
            borderColor: 'var(--line-2, #cec3a5)', color: 'var(--ink-2, #3d463a)',
            borderRadius: 10, marginRight: 8, textTransform: 'none', fontWeight: 600
          }}>
          Seed Vendors
        </Button>

        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setModalItem(null); setModalOpen(true) }}
          style={{ background: 'var(--primary, #1f5e3a)', color: '#fbf8f1', borderRadius: 10 }}>
          Add Rate
        </Button>
      </div>

      {/* Compare Hauls Slider */}
      {filteredRates.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16,
          padding: '8px 16px', background: 'var(--surface, #fbf8f1)',
          border: '1px solid var(--line, #e0d8c2)', borderRadius: 10
        }}>
          <span style={{ ...mono, fontSize: 11, fontWeight: 600, color: 'var(--ink-3, #6f7a6b)', whiteSpace: 'nowrap' }}>
            Compare for
          </span>
          <Slider
            value={compareHauls} min={1} max={10} step={1}
            onChange={(e, val) => setCompareHauls(val)}
            style={{ color: 'var(--primary, #1f5e3a)', maxWidth: 200 }}
          />
          <span style={{ ...mono, fontSize: 13, fontWeight: 700, color: 'var(--primary, #1f5e3a)', whiteSpace: 'nowrap' }}>
            {compareHauls} haul{compareHauls > 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Seed Result */}
      {seedResult && (
        <Box mb={2} p={1.5} style={{
          backgroundColor: seedResult.error ? 'var(--disposal-soft, #f7dccf)' : 'var(--primary-soft, #e3eee0)',
          borderRadius: 'var(--r, 10px)', border: `1px solid ${seedResult.error ? 'var(--disposal, #a9341d)' : 'var(--primary, #1f5e3a)'}22`
        }}>
          <Typography variant="body2" style={{ color: seedResult.error ? 'var(--disposal, #a9341d)' : 'var(--primary, #1f5e3a)' }}>
            {seedResult.error || seedResult.message}
          </Typography>
        </Box>
      )}

      {/* Table */}
      {loading ? (
        <Box textAlign="center" py={6}><CircularProgress style={{ color: 'var(--primary, #1f5e3a)' }} /></Box>
      ) : filteredRates.length === 0 ? (
        <Box textAlign="center" py={6}>
          <div style={{ color: 'var(--ink-4, #97a094)', fontSize: 40, marginBottom: 8 }}>
            {rates.length === 0 ? '\u{1F4E6}' : '\u{1F50D}'}
          </div>
          <Typography variant="h6" style={{ color: 'var(--ink-3, #6f7a6b)', marginBottom: 8, fontWeight: 600 }}>
            {rates.length === 0
              ? 'No roll-off rates yet'
              : 'No rates match your filters'}
          </Typography>
          {rates.length === 0 && (
            <div>
              <Typography variant="body2" style={{ color: 'var(--ink-4, #97a094)', marginBottom: 16 }}>
                Add your first vendor rate or seed demo data to get started.
              </Typography>
              <Button variant="outlined" size="small" onClick={handleSeed} disabled={seeding}
                style={{ borderColor: 'var(--primary, #1f5e3a)', color: 'var(--primary, #1f5e3a)', borderRadius: 10 }}>
                {seeding ? 'Seeding...' : 'Seed Demo Vendors'}
              </Button>
            </div>
          )}
        </Box>
      ) : (
        <div style={{
          background: 'var(--surface, #fbf8f1)', border: '1px solid var(--line, #e0d8c2)',
          borderRadius: 'var(--r-lg, 14px)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden'
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 40 }} />
                <TableCell>Vendor</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Haul Fee</TableCell>
                <TableCell align="center">Type</TableCell>
                <TableCell align="right">
                  <Tooltip title={`Total cost for ${compareHauls} haul${compareHauls > 1 ? 's' : ''} at included weight`}>
                    <span>Effective ({compareHauls}x)</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Waste</TableCell>
                <TableCell>Quoted</TableCell>
                <TableCell style={{ width: 80 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRates.map(item => {
                const cc = CONTAINER_COLORS[item.containerSize] || { bg: '#f0f0f0', color: '#666', border: '#ccc' }
                const effectiveCost = calcEffectiveCost(item, compareHauls)
                const pt = getPricingType(item)
                const ptc = PRICING_TYPE_COLORS[pt]
                return (
                <React.Fragment key={item._id}>
                  <TableRow hover style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedId(expandedId === item._id ? null : item._id)}>
                    <TableCell>
                      {expandedId === item._id
                        ? <ExpandLessIcon fontSize="small" style={{ color: 'var(--ink-4, #97a094)' }} />
                        : <ExpandMoreIcon fontSize="small" style={{ color: 'var(--ink-4, #97a094)' }} />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--ink, #1c2419)' }}>{item.vendorName || 'Unknown'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={CONTAINER_LABELS[item.containerSize] || `${item.containerSize} CY`}
                        style={{
                          background: cc.bg, color: cc.color, fontWeight: 700,
                          border: `1px solid ${cc.border}`, fontFamily: 'var(--font-mono)',
                          fontSize: 10.5, letterSpacing: '0.04em'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      {editingPriceId === item._id ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                          <input
                            type="number" value={editingPriceVal}
                            onChange={e => setEditingPriceVal(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleInlinePriceSave(item._id); if (e.key === 'Escape') setEditingPriceId(null) }}
                            autoFocus
                            style={{
                              width: 80, fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700,
                              textAlign: 'right', borderColor: 'var(--primary, #1f5e3a)',
                              background: '#fff', boxShadow: '0 0 0 3px rgba(31,94,58,.15)',
                              padding: '4px 7px', borderRadius: 6, border: '1px solid var(--primary, #1f5e3a)', outline: 'none'
                            }}
                          />
                          <IconButton size="small" onClick={() => handleInlinePriceSave(item._id)}><CheckIcon fontSize="small" style={{ color: 'var(--primary, #1f5e3a)' }} /></IconButton>
                          <IconButton size="small" onClick={() => setEditingPriceId(null)}><CancelIcon fontSize="small" /></IconButton>
                        </div>
                      ) : (
                        <Tooltip title="Click to update haul fee">
                          <span style={{
                            ...mono, fontWeight: 700, fontSize: 14,
                            color: 'var(--ink, #1c2419)', cursor: 'pointer',
                            padding: '3px 7px', borderRadius: 6, border: '1px solid transparent',
                            transition: 'all .12s ease'
                          }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(28,36,25,.04)'; e.currentTarget.style.borderColor = 'var(--line, #e0d8c2)' }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent' }}
                            onClick={() => { setEditingPriceId(item._id); setEditingPriceVal(item.haulFee || '') }}>
                            {$2(item.haulFee)}
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={PRICING_TYPE_LABELS[pt]}
                        style={{ background: ptc.bg, color: ptc.color, fontWeight: 600, fontSize: 10, height: 20 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <span style={{ ...mono, fontWeight: 700, fontSize: 14, color: 'var(--accent, #c66a45)' }}>
                        {$2(effectiveCost)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, maxWidth: 180 }}>
                        {(item.acceptedWaste || []).slice(0, 3).map(w => {
                          const wc = WASTE_COLORS[w] || { bg: '#f0f0f0', color: '#666' }
                          return (
                            <Chip key={w} size="small" label={WASTE_LABELS[w] || w}
                              style={{
                                background: wc.bg, color: wc.color,
                                fontSize: 10, height: 20, fontWeight: 600,
                                border: `1px solid ${wc.color}22`
                              }}
                            />
                          )
                        })}
                        {(item.acceptedWaste || []).length > 3 && (
                          <Chip size="small" label={`+${item.acceptedWaste.length - 3}`}
                            style={{ background: 'var(--bg-2, #ece5d3)', color: 'var(--ink-3, #6f7a6b)', fontSize: 10, height: 20 }}
                          />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Tooltip title={item.quoteDate ? new Date(item.quoteDate).toLocaleDateString() : 'No quote date'}>
                          <span style={{ ...mono, fontSize: 12, color: 'var(--ink-3, #6f7a6b)' }}>
                            {relativeDate(item.quoteDate)}
                          </span>
                        </Tooltip>
                        {isStaleQuote(item.quoteDate) && (
                          <Tooltip title="Quote may be stale (> 6 months)">
                            <WarningIcon fontSize="small" style={{ color: 'var(--equipment, #b35d1a)' }} />
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => { setModalItem(item); setModalOpen(true) }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => setDeleteTarget(item)}>
                        <DeleteIcon fontSize="small" style={{ color: 'var(--disposal, #a9341d)' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={9} style={{ padding: 0, borderBottom: expandedId === item._id ? undefined : 'none' }}>
                      <Collapse in={expandedId === item._id} unmountOnExit>
                        <RateDetailsPanel item={item} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              )})}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Form Modal */}
      <RateFormModal open={modalOpen} onClose={() => setModalOpen(false)}
        onSave={() => { fetchRates(); fetchSummary() }} editItem={modalItem} url={url} />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        PaperProps={{ style: { borderRadius: 14 } }}>
        <DialogTitle style={{ color: 'var(--ink, #1c2419)' }}>Deactivate Roll-off Rate</DialogTitle>
        <DialogContent>
          <Typography style={{ color: 'var(--ink-2, #3d463a)' }}>
            Deactivate the {CONTAINER_LABELS[deleteTarget?.containerSize]} rate for "{deleteTarget?.vendorName}"?
            This will hide it from active queries but preserve the record.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setDeleteTarget(null)} style={{ color: 'var(--ink-3, #6f7a6b)' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained"
            style={{ background: 'var(--disposal, #a9341d)', color: '#fff', borderRadius: 10 }}>Deactivate</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default RolloffRatesTable
